import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { trackEvent } from '@repo/analytics/src/ga';
import { addAsyncState, initAsyncState } from '@repo/store';

import { AuthState, SignUpData } from '@/store/auth/types';
import { createAppAsyncThunk } from '@/store/createAppAsyncThunk';
import { ApiResponse } from '@/types/types';
import api from '@/utils/api';
import { postLoginAccountEndpoint, postLogoutEndpoint, postSignUpEndpoint } from '@/utils/endpoints';

export const authSliceName = 'auth';

// Where sessions from before 2026-10-07 kept the flag; the store's persisted state wins once written.
const legacyLoggedIn = () => {
  try {
    return Boolean(localStorage.getItem('iv_logged_in'));
  } catch {
    return false;
  }
};

const INITIAL_STATE: AuthState = {
  loggedIn: legacyLoggedIn(),
  login: initAsyncState(),
  signUp: initAsyncState(),
  explicitLogout: false,
};

export const login = createAppAsyncThunk(
  `${authSliceName}/login`,
  async ({ account, password }: { account: string; password: string }, { dispatch, rejectWithValue }) => {
    try {
      const { status, message } = (await api.request<ApiResponse>(postLoginAccountEndpoint(account, password))).data;
      if (status !== 1) return rejectWithValue(message || 'Could not log in');

      dispatch(loginUser());
    } catch (e: any) {
      if (!e.response) console.error(e);
      return rejectWithValue(e.response?.data?.message || 'Could not log in');
    }
  },
  { condition: (_, { getState }) => !getState()[authSliceName].login.loading }
);

export const signUp = createAppAsyncThunk(
  `${authSliceName}/signUp`,
  async (inputs: SignUpData, { dispatch, rejectWithValue }) => {
    try {
      const { message } = (await api.request<ApiResponse>(postSignUpEndpoint(inputs))).data;
      trackEvent('sign_up', { method: 'email' });
      dispatch(loginUser());

      return message || 'Your account has been created';
    } catch (e: any) {
      if (!e.response) console.error(e);
      return rejectWithValue(e.response?.data?.message || 'Could not create account');
    }
  },
  { condition: (_, { getState }) => !getState()[authSliceName].signUp.loading }
);

/** The user asked to leave. A dead token dispatches `logoutUser(false)` instead. */
export const logout = createAppAsyncThunk(`${authSliceName}/logout`, async (_: void, { dispatch }) => {
  try {
    await api.request(postLogoutEndpoint());
  } catch (e: any) {
    if (!e.response) console.error('logout failed', e);
  }
  dispatch(logoutUser(true));
});

export const authSlice = createSlice({
  name: authSliceName,
  initialState: INITIAL_STATE satisfies AuthState as AuthState,
  reducers: {
    // The session itself is the httpOnly cookie, so the store keeps only whether there is one.
    loginUser(state) {
      state.loggedIn = true;
      state.explicitLogout = false;
    },
    // Did the user ask to leave? `AuthRoute` only carries a `redirect_to` when they did not.
    logoutUser(state, { payload: explicit }: PayloadAction<boolean>) {
      state.loggedIn = false;
      state.explicitLogout = explicit;
    },
  },
  extraReducers: (builder) => {
    addAsyncState(builder, login, (state, asyncState) => {
      state.login = asyncState;
    });
    addAsyncState(builder, signUp, (state, asyncState) => {
      state.signUp = asyncState;
    });
  },
});

export const { loginUser, logoutUser } = authSlice.actions;
